import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AccessToken } from '@eda/shared/models/apigee.model';
import { CacheFactoryService } from '@eda/shared/cache/cache-factory';
import { map, Observable, switchMap, tap, throwError } from 'rxjs';
import { OktaAuthService } from './okta-auth.service';

@Injectable({
  providedIn: 'root',
})
export class ApigeeTokenGenerator {
  private httpClient = inject(HttpClient);
  private cacheFactory = inject(CacheFactoryService);
  private oktaAuthService = inject(OktaAuthService);
  private readonly cacheKey = 'apigee_token_cache_key';
  private readonly apigeeTokenEndpoint = '/api/apigeetokengenerator';
  private readonly secondsToMinutes = 60;

  /**
   * Returns an Apigee token from cache when available, otherwise requests a new one.
   * A fresh token request is executed only after Okta access token retrieval succeeds.
   */
  generateApigeeToken(): Observable<AccessToken> {
    const cachedToken = this.getCachedToken();
    if (cachedToken) {
      return cachedToken;
    }

    return this.oktaAuthService.getAccessToken().pipe(
      switchMap((oktaToken) => {
        const accessToken = oktaToken?.accessToken;
        if (!accessToken) {
          return throwError(() => new Error('Unauthorized'));
        }
        return this.requestForToken(accessToken);
      }),
      tap((token) => this.cacheToken(token)),
    );
  }

  /**
   * Returns cached Apigee token if present; otherwise returns null.
   */
  private getCachedToken(): Observable<AccessToken> | null {
    if (!this.cacheFactory.isCacheAvailable(this.cacheKey)) {
      return null;
    }

    return this.cacheFactory.getCache(this.cacheKey).pipe(map((token) => token as AccessToken));
  }

  /**
   * Caches the Apigee token using its expiry converted from seconds to minutes.
   */
  private cacheToken(token: AccessToken): void {
    this.cacheFactory.setCacheable({
      key: this.cacheKey,
      toCache: token,
      ttl: token.expires_in / this.secondsToMinutes,
    });
  }

  /**
   * Requests a new Apigee token from the backend using the Okta access token.
   */
  private requestForToken(accessToken: string): Observable<AccessToken> {
    return this.httpClient.post<AccessToken>(this.apigeeTokenEndpoint, null, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }
}
